import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buildAppMeta } from "@/lib/seo";

export const Route = createFileRoute("/c/$token")({
  head: ({ params }) => ({
    meta: buildAppMeta({
      title: "Your lesson balance",
      description: "View your remaining lesson credits.",
      path: `/c/${params.token}`,
    }),
  }),
  component: PublicBalancePage,
});

type Pkg = {
  id: string;
  name: string;
  lessons_remaining: number;
  lessons_total: number;
  expires_at: string | null;
  status: string;
};

type Activity = {
  kind: string;
  delta: number;
  balance_after: number;
  created_at: string;
};

type Payload = {
  client: { name: string };
  coach: {
    business_name: string | null;
    full_name: string | null;
    connected_email: string | null;
  };
  packages: Pkg[];
  recent_activity: Activity[];
};

const KIND_LABEL: Record<string, string> = {
  purchase: "Pack purchased",
  consume: "Lesson used",
  refund: "Refund",
  manual_adjustment: "Adjustment",
  comp: "Complimentary lesson",
  expire: "Pack expired",
  reversal: "Reversed",
};

function PublicBalancePage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: res, error } = await supabase.rpc("get_public_balance" as never, {
        _token: token,
      } as never);
      if (error || !res) {
        setNotFound(true);
      } else {
        setData(res as unknown as Payload);
      }
      setLoading(false);
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold">Balance unavailable</h1>
          <p className="text-sm text-muted-foreground mt-2">
            This link is no longer active. Please contact your coach for an updated link.
          </p>
        </Card>
      </div>
    );
  }

  const coachName = data.coach.business_name || data.coach.full_name || "Your coach";
  const activePacks = data.packages.filter((p) => p.status === "active");
  const totalRemaining = activePacks.reduce((s, p) => s + p.lessons_remaining, 0);

  return (
    <div className="min-h-screen bg-muted/30 p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center pt-4">
          <p className="text-sm text-muted-foreground">{coachName}</p>
          <h1 className="text-2xl font-semibold mt-1">{data.client.name}</h1>
        </div>

        <Card className="p-6 text-center">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Lessons remaining
          </p>
          <p className="text-5xl font-bold tabular-nums mt-2">{totalRemaining}</p>
          {data.packages.length === 0 && (
            <p className="text-sm text-muted-foreground mt-3">
              No active packages. Contact your coach to renew.
            </p>
          )}
        </Card>

        {data.packages.length > 0 && (
          <Card className="p-4">
            <h2 className="text-sm font-semibold mb-3">Your packages</h2>
            <div className="space-y-2">
              {data.packages.map((p) => {
                const exp = p.expires_at ? new Date(p.expires_at) : null;
                const daysToExp = exp
                  ? Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                  : null;
                const expSoon = daysToExp !== null && daysToExp <= 30 && daysToExp >= 0;
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between border rounded-md p-3"
                  >
                    <div>
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.lessons_remaining} of {p.lessons_total} remaining
                        {p.status === "pending_payment" && " · payment pending"}
                      </div>
                    </div>
                    {expSoon && (
                      <Badge variant="outline" className="text-xs">
                        Expires in {daysToExp}d
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {data.recent_activity.length > 0 && (
          <Card className="p-4">
            <h2 className="text-sm font-semibold mb-3">Recent activity</h2>
            <div className="space-y-2">
              {data.recent_activity.map((a, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0"
                >
                  <div>
                    <div>{KIND_LABEL[a.kind] ?? a.kind}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="tabular-nums text-muted-foreground">
                    {a.delta > 0 ? `+${a.delta}` : a.delta} · {a.balance_after}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {data.coach.connected_email && (
          <div className="text-center pt-2">
            <Button asChild variant="outline" size="sm">
              <a href={`mailto:${data.coach.connected_email}?subject=Renewing my lesson pack`}>
                Renew with {coachName}
              </a>
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground pt-4">
          Powered by CoachQuick
        </p>
      </div>
    </div>
  );
}
